import {
  FormPagePropertyItemDateDefaultValue,
  FormPagePropertyItemDateShowRange,
} from 'backend';
import {
  DAY,
  MONTH,
  YEAR_PULLDOWN_DEFAULT_FROM,
  YEAR_PULLDOWN_DEFAULT_TO,
} from 'models';
import {
  DateProperty,
  DatePropertyYearPulldown,
  DATE_PROPERTY_INPUT_TYPE,
  FormPageInputItem,
  FormPageInputItemDate,
  FormPageInputItemDateFree,
  FormPageInputItemDateYearPulldown,
  INPUT_ITEM_TYPE,
} from 'shared';

const handleFree = (
  formPageInputItem?: FormPageInputItem,
): FormPageInputItemDateFree => {
  const inputItemType = INPUT_ITEM_TYPE.dateFree;
  if (formPageInputItem?.inputItemType === INPUT_ITEM_TYPE.dateFree) {
    return {
      inputItemType,
      year: formPageInputItem.year || '',
      month: formPageInputItem.month || '',
      day: formPageInputItem.day || '',
    };
  }
  return {
    inputItemType,
    year: '',
    month: '',
    day: '',
  };
};

const isDatePulldownSelected = (
  formPageInputItem?: FormPageInputItem,
): formPageInputItem is FormPageInputItemDateYearPulldown =>
  formPageInputItem?.inputItemType === INPUT_ITEM_TYPE.dateYearPulldown;

const getYearFrom = (showRange?: FormPagePropertyItemDateShowRange): string => {
  const from = showRange?.showYearFrom ?? YEAR_PULLDOWN_DEFAULT_FROM;
  return `${from}`;
};

const getDateDefaultValue = (
  dateField: 'year' | 'month' | 'day',
  defaultValue: FormPagePropertyItemDateDefaultValue | undefined,
) => {
  if (defaultValue && defaultValue[dateField]) {
    return dateField !== 'year'
      ? String(defaultValue[dateField]).padStart(2, '0')
      : String(defaultValue[dateField]);
  }
  return '';
};

/**
 * 年の入力値が有効な範囲内かどうかを判定し、年の有効な範囲内であれば入力値を文字列にして返し、範囲外であれば空文字を返す
 * @param yearValue 年の入力値
 * @param showRange 年の有効な範囲内
 * @returns 年の有効な範囲内であれば入力値を文字列にして返し、範囲外であれば空文字を返す
 */
const getValidYear = (
  yearValue: string | undefined,
  showRange?: FormPagePropertyItemDateShowRange,
): string => {
  if (!yearValue) return '';
  const yearIntValue = parseInt(String(yearValue), 10);
  if (!yearIntValue || String(yearValue) !== String(yearIntValue)) return '';
  const from = showRange?.showYearFrom ?? YEAR_PULLDOWN_DEFAULT_FROM;
  const to = showRange?.showYearTo ?? YEAR_PULLDOWN_DEFAULT_TO;
  if (from <= yearIntValue && yearIntValue <= to) {
    return `${yearIntValue}`;
  }
  return '';
};

const handleYearPulldown = (
  inputFormat: DatePropertyYearPulldown['inputFormat'],
  formPageInputItem?: FormPageInputItem,
): FormPageInputItemDateYearPulldown => {
  const { showRange, unselectedState, defaultValue } = inputFormat;
  const inputItemType = INPUT_ITEM_TYPE.dateYearPulldown;
  if (unselectedState) {
    // isDatePulldownSelected が true = 既にユーザが確認画面に遷移して戻ってきた場合 or 変更フォームにログインして顧客マスタの値がセットされている場合
    // isDatePulldownSelected が false = 年月日プルダウン型項目に入力値が存在しない場合（入力画面に最初に遷移してきた場合）
    if (isDatePulldownSelected(formPageInputItem)) {
      return {
        inputItemType,
        year: getValidYear(formPageInputItem.year, showRange) || '',
        month: formPageInputItem.month || '',
        day: formPageInputItem.day || '',
      };
    }
    return {
      inputItemType,
      year:
        getValidYear(getDateDefaultValue('year', defaultValue), showRange) ||
        '',
      month: getDateDefaultValue('month', defaultValue) || '',
      day: getDateDefaultValue('day', defaultValue) || '',
    };
  }
  const yearFrom = getYearFrom(showRange);
  if (isDatePulldownSelected(formPageInputItem)) {
    return {
      inputItemType,
      year: getValidYear(formPageInputItem.year, showRange) || `${yearFrom}`,
      month: formPageInputItem.month || MONTH[0],
      day: formPageInputItem.day || DAY[0],
    };
  }
  return {
    inputItemType,
    year:
      getValidYear(getDateDefaultValue('year', defaultValue), showRange) ||
      `${yearFrom}`,
    month: getDateDefaultValue('month', defaultValue) || MONTH[0],
    day: getDateDefaultValue('day', defaultValue) || DAY[0],
  };
};

export const convertDateInitialValue = (
  property: DateProperty,
  formPageInputItem?: FormPageInputItem,
): FormPageInputItemDate => {
  const { free, yearPulldown } = DATE_PROPERTY_INPUT_TYPE;
  switch (property.inputFormat.inputType) {
    case free: {
      return handleFree(formPageInputItem);
    }
    case yearPulldown:
      return handleYearPulldown(property.inputFormat, formPageInputItem);
  }
};
